import {
	useGetAllLocalizationCountriesQuery,
	useGetLocalizationStatesQuery,
	useGetPlaceDetailsLazyQuery,
} from '@codezo/graphql';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AddressAutocomplete } from './address-autocomplete';
import { Field } from './field';
import { Input } from './input';
import { SelectPopover } from './select-popover';

export interface AddressData {
	addressLine1: string;
	addressLine2: string;
	city: string;
	stateCode: string;
	postalCode: string;
	countryCode: string;
	googlePlaceId: string;
	latitude: number | null;
	longitude: number | null;
}

export const EMPTY_ADDRESS: AddressData = {
	addressLine1: '',
	addressLine2: '',
	city: '',
	stateCode: '',
	postalCode: '',
	countryCode: '',
	googlePlaceId: '',
	latitude: null,
	longitude: null,
};

/** Per-field messages, keyed the same as the data — what `validateAddress` returns. */
export type AddressErrors = Partial<Record<keyof AddressData, string>>;

/**
 * The floor every consumer of this form needs: an address the API will accept.
 * `stateCode` is only required where the country says it is, which is why the
 * form reports its own country back through `requireState`.
 */
export function validateAddress(
	value: AddressData,
	options?: { requireState?: boolean; stateLabel?: string }
): AddressErrors {
	const errors: AddressErrors = {};
	if (!value.addressLine1.trim()) errors.addressLine1 = 'Street address is required';
	if (!value.city.trim()) errors.city = 'City is required';
	if (!value.postalCode.trim()) errors.postalCode = 'Postal code is required';
	// A country is picked from a list now, so the only failure left is not picking.
	if (!value.countryCode.trim()) errors.countryCode = 'Select a country';
	if (options?.requireState && !value.stateCode.trim()) {
		errors.stateCode = `${options.stateLabel ?? 'State'} is required`;
	}
	return errors;
}

interface AddressFormProps {
	value: AddressData;
	onChange: (address: AddressData) => void;
	/** Validation messages to show, usually from `validateAddress` on submit. */
	errors?: AddressErrors;
	/**
	 * Reports the picked country's address rules back up, so the caller can
	 * validate the state field it can't see the requirement for.
	 */
	onCountryRulesChange?: (rules: { requireState: boolean; stateLabel: string }) => void;
}

export function AddressForm({ value, onChange, errors, onCountryRulesChange }: AddressFormProps) {
	const [sessionToken] = useState(() => crypto.randomUUID());
	const [getPlaceDetails] = useGetPlaceDetailsLazyQuery();

	const { data: countriesData } = useGetAllLocalizationCountriesQuery({
		fetchPolicy: 'no-cache',
	});
	const countries = countriesData?.getAllLocalizationCountries;

	const selectedCountry = useMemo(
		() => countries?.find((c) => c.code === value.countryCode) ?? null,
		[countries, value.countryCode]
	);

	const { data: statesData } = useGetLocalizationStatesQuery({
		variables: { countryCode: value.countryCode },
		skip: !value.countryCode || !selectedCountry?.hasStates,
		fetchPolicy: 'no-cache',
	});

	const countryOptions = useMemo(
		() => countries?.map((c) => ({ value: c.code, label: c.name })) ?? [],
		[countries]
	);

	const stateOptions = useMemo(
		() =>
			statesData?.getLocalizationStates?.map((s) => ({ value: s.code, label: s.name })) ?? [],
		[statesData]
	);

	// Country-specific wording — "ZIP code" / "Province" and friends, per country.
	const postalCodeLabel = selectedCountry?.addressFormat?.postalCodeLabel || 'Postal code';
	const stateLabel = selectedCountry?.addressFormat?.stateLabel || 'State';
	const requireState =
		!!selectedCountry?.hasStates && !!selectedCountry.addressFormat?.stateRequired;

	// Handed to the caller whenever the country changes it: the caller runs the
	// validation, but only the country data loaded in here knows the rules.
	useEffect(() => {
		onCountryRulesChange?.({ requireState, stateLabel });
	}, [requireState, stateLabel, onCountryRulesChange]);

	const handleAddressSelect = useCallback(
		async (placeId: string) => {
			const { data } = await getPlaceDetails({ variables: { placeId, sessionToken } });
			if (!data?.getPlaceDetails) return;

			const d = data.getPlaceDetails;
			const line1 = [d.streetNumber, d.streetName].filter(Boolean).join(' ');

			onChange({
				addressLine1: line1 || d.formattedAddress || '',
				addressLine2: '',
				city: d.city || '',
				stateCode: d.stateCode || '',
				postalCode: d.postalCode || '',
				countryCode: d.countryCode || '',
				googlePlaceId: d.placeId || '',
				latitude: d.latitude ?? null,
				longitude: d.longitude ?? null,
			});
		},
		[getPlaceDetails, sessionToken, onChange]
	);

	const update = (field: keyof AddressData, v: string) => {
		onChange({ ...value, [field]: v });
	};

	return (
		<div className="flex flex-col gap-3">
			<Field label="Search address">
				{/* Scoped to the picked country, so the suggestions can't quietly
				    reintroduce a different one. */}
				<AddressAutocomplete
					onSelect={handleAddressSelect}
					countryCode={value.countryCode || null}
				/>
			</Field>

			<Field label="Country" required error={errors?.countryCode}>
				<SelectPopover
					value={value.countryCode}
					// A state belongs to the country it was picked in — keeping it
					// across a country change sends a code the new country never had.
					onChange={(v) => onChange({ ...value, countryCode: v, stateCode: '' })}
					options={countryOptions}
					placeholder="Select country"
				/>
			</Field>

			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<Field label="Address line 1" required error={errors?.addressLine1}>
					<Input
						value={value.addressLine1}
						onChange={(e) => update('addressLine1', e.target.value)}
						placeholder="Street address"
					/>
				</Field>
				<Field label="Address line 2">
					<Input
						value={value.addressLine2}
						onChange={(e) => update('addressLine2', e.target.value)}
						placeholder="Apt, suite, etc."
					/>
				</Field>
				<Field label="City" required error={errors?.city}>
					<Input
						value={value.city}
						onChange={(e) => update('city', e.target.value)}
						placeholder="City"
					/>
				</Field>
				<Field label={postalCodeLabel} required error={errors?.postalCode}>
					<Input
						value={value.postalCode}
						onChange={(e) => update('postalCode', e.target.value)}
						placeholder={postalCodeLabel}
					/>
				</Field>
				{selectedCountry?.hasStates && (
					<Field label={stateLabel} required={requireState} error={errors?.stateCode}>
						<SelectPopover
							value={value.stateCode}
							onChange={(v) => update('stateCode', v)}
							options={stateOptions}
							placeholder={`Select ${stateLabel.toLowerCase()}`}
						/>
					</Field>
				)}
			</div>
		</div>
	);
}
